import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Bug, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { DiscordIcon, DISCORD_INVITE_URL } from "@/components/SettingsDrawer";

// Section content blocks: strings render as paragraphs, arrays as bullet
// lists, { sub } as a sub-heading, { note } as an emphasized short line.
const DOCS = {
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Privacy",
    meta: ["Effective Date: 20.07.2026", "Last Updated: 20.07.2026"],
    intro: [
      "Welcome to Continua.",
      "Your privacy is important to us. This Privacy Policy explains what information we collect, how we use it, how we protect it, and the choices you have regarding your data when using Continua.",
      "By using Continua, you agree to this Privacy Policy.",
    ],
    footer: "© Continua. All rights reserved.",
    sections: [
      {
        h: "1. About Continua",
        blocks: [
          "Continua is an AI-powered companion application that provides immersive conversations, relaxation experiences, and persistent character relationships.",
          "Some features use artificial intelligence to generate conversations and maintain continuity between sessions.",
        ],
      },
      {
        h: "2. Information We Collect",
        blocks: [
          "We may collect the following information:",
          { sub: "Account Information" },
          [
            "Email address",
            "Encrypted password (hashed using bcrypt)",
            "Account creation date",
            "Subscription status",
            "Login history",
          ],
          { note: "We never store your password in plain text." },
          { sub: "Character Memory" },
          "To provide persistent conversations, Continua stores information that characters remember about you, including:",
          [
            "Your chosen name",
            "Conversation history",
            "Character relationship progress",
            "Memories created during interactions",
            "User preferences relevant to conversations",
          ],
          "This information exists solely to improve continuity between sessions.",
          { sub: "Session Information" },
          "We may store:",
          [
            "Session duration",
            "Session timestamps",
            "Selected character",
            "Generated conversation metadata",
            "Anonymous usage statistics",
          ],
          { sub: "Device Information" },
          "We may automatically collect:",
          [
            "Device model",
            "Operating system version",
            "App version",
            "Crash logs",
            "Performance diagnostics",
          ],
          "This information helps improve application stability.",
        ],
      },
      {
        h: "3. AI Generated Content",
        blocks: [
          "Conversations inside Continua are generated using artificial intelligence.",
          "Messages may be processed by third-party AI providers in order to generate responses.",
          "We do not use your conversations to intentionally identify you as an individual.",
        ],
      },
      {
        h: "4. Analytics",
        blocks: [
          "We collect anonymous analytics to improve Continua.",
          "Examples include:",
          [
            "Session completion rate",
            "Feature usage",
            "Crash reports",
            "Performance metrics",
            "Error statistics",
          ],
          "Analytics are used solely to improve the application.",
        ],
      },
      {
        h: "5. Audio",
        blocks: [
          "Some sessions include ambient sounds or generated voice.",
          "Audio preferences may be stored locally on your device to improve your experience.",
        ],
      },
      {
        h: "6. Cookies & Local Storage",
        blocks: [
          "Continua may store information locally on your device, including:",
          [
            "Login session",
            "User preferences",
            "Cached assets",
            "Downloaded audio",
            "Application settings",
          ],
          "This data helps improve loading speed and user experience.",
        ],
      },
      {
        h: "7. Data Security",
        blocks: [
          "We use industry-standard security practices, including:",
          [
            "Encrypted connections (HTTPS)",
            "Password hashing (bcrypt)",
            "Authentication tokens",
            "Secure backend infrastructure",
          ],
          "While we strive to protect your information, no internet service can guarantee absolute security.",
        ],
      },
      {
        h: "8. Data Retention",
        blocks: [
          "Your information is stored only for as long as necessary to provide Continua's services.",
          "You may request deletion of your account and associated data.",
          "Certain anonymous analytics may remain after account deletion.",
        ],
      },
      {
        h: "9. Third-Party Services",
        blocks: [
          "Continua may use trusted third-party services, including but not limited to:",
          [
            "AI providers",
            "Authentication infrastructure",
            "Cloud storage",
            "Analytics providers",
            "Payment processors",
            "Email delivery services",
          ],
          "Each provider processes data according to its own privacy policy.",
        ],
      },
      {
        h: "10. Children's Privacy",
        blocks: [
          "Continua is not intended for children under the age required by local law.",
          "If we become aware that personal information has been collected from a child without appropriate consent, we will delete such information.",
        ],
      },
      {
        h: "11. International Data Transfers",
        blocks: [
          "Your information may be processed on servers located in countries other than your own.",
          "By using Continua, you acknowledge that your information may be transferred internationally where necessary to provide the service.",
        ],
      },
      {
        h: "12. Your Rights",
        blocks: [
          "Depending on your jurisdiction, you may have the right to:",
          [
            "Access your personal information",
            "Correct inaccurate information",
            "Delete your account",
            "Request a copy of your stored data",
            "Withdraw consent where applicable",
          ],
          "Requests may be submitted through the Support section.",
        ],
      },
      {
        h: "13. Changes to This Policy",
        blocks: [
          "We may update this Privacy Policy from time to time.",
          "Significant changes will be communicated through the application or other appropriate means.",
          "The latest version will always be available within Continua.",
        ],
      },
      {
        h: "14. Contact",
        blocks: [
          "If you have questions regarding this Privacy Policy, please contact us through the Support section inside Continua.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    eyebrow: "Privacy",
    meta: ["Effective Date: 20.07.2026", "Last Updated: 20.07.2026"],
    intro: [
      "Welcome to Continua.",
      "These Terms of Service (\u201cTerms\u201d) govern your access to and use of Continua, including its website, mobile applications, AI-powered companion features, and related services (collectively, the \u201cService\u201d).",
      "By creating an account or using Continua, you agree to be bound by these Terms.",
      "If you do not agree with these Terms, please do not use Continua.",
    ],
    footer: "© Continua. All rights reserved.",
    sections: [
      {
        h: "1. Eligibility",
        blocks: [
          "To use Continua, you must:",
          [
            "Meet the minimum age required under the laws of your country or region.",
            "Have the legal capacity to enter into these Terms.",
            "Comply with all applicable laws while using the Service.",
          ],
          "If you are under the required age, you may only use Continua with the permission of a parent or legal guardian where permitted by law.",
        ],
      },
      {
        h: "2. Your Account",
        blocks: [
          "Creating an account allows Continua to provide persistent conversations, saved memories, personalized settings, and premium features.",
          "You agree to:",
          [
            "Provide accurate registration information.",
            "Keep your login credentials secure.",
            "Notify us if you believe your account has been compromised.",
            "Be responsible for all activity occurring under your account.",
          ],
          "You may not share your account in a way that violates these Terms or attempts to bypass subscription limits.",
        ],
      },
      {
        h: "3. Acceptable Use",
        blocks: [
          "You agree to use Continua responsibly.",
          "You must not:",
          [
            "Use the Service for unlawful purposes.",
            "Attempt to gain unauthorized access to our systems.",
            "Reverse engineer, decompile, exploit, or interfere with the application or backend.",
            "Circumvent subscription or payment systems.",
            "Distribute malware or harmful software through the Service.",
            "Attempt to disrupt, overload, or damage Continua's infrastructure.",
            "Use automated tools to abuse or excessively access the Service.",
            "Impersonate another individual or organization.",
            "Violate the rights or privacy of other users.",
          ],
          "Violation of these Terms may result in suspension or permanent termination of your account.",
        ],
      },
      {
        h: "4. AI Conversations",
        blocks: [
          "Continua provides conversations generated using artificial intelligence.",
          "While we strive to provide engaging and consistent experiences:",
          [
            "AI-generated responses may contain inaccuracies.",
            "Characters may occasionally misunderstand, forget, or incorrectly remember information.",
            "AI responses should not be interpreted as factual, medical, psychological, legal, financial, or professional advice.",
            "Continua should not be relied upon during emergencies or situations requiring professional assistance.",
          ],
          "The primary purpose of Continua is entertainment, companionship, relaxation, and immersive storytelling.",
        ],
      },
      {
        h: "5. Character Memory",
        blocks: [
          "One of Continua's core features is persistent character memory.",
          "Characters may remember:",
          [
            "Previous conversations;",
            "Relationship progression;",
            "User preferences;",
            "Information voluntarily shared by the user.",
          ],
          "Although we continuously improve memory quality, persistent memories are not guaranteed to be perfect.",
          "Occasionally, characters may forget, misremember, or inconsistently reference previous interactions due to the nature of AI-generated content.",
          "Such behavior does not necessarily indicate a software defect.",
        ],
      },
      {
        h: "6. Premium Subscription",
        blocks: [
          "Some features of Continua require an active Premium subscription.",
          "Premium subscriptions:",
          [
            "Renew automatically unless cancelled.",
            "Can be managed through your App Store or Google Play account.",
            "May change over time as Continua evolves and new features are introduced.",
          ],
          "Subscription prices are displayed before purchase.",
          "By purchasing Premium, you authorize the applicable platform (Apple App Store or Google Play) to process recurring subscription payments according to their billing policies.",
        ],
      },
      {
        h: "7. Intellectual Property",
        blocks: [
          "Continua, including its software, user interface, visual design, characters, branding, logos, artwork, audio, and other original content, is protected by applicable intellectual property laws.",
          "Unless otherwise stated, all rights to Continua remain the property of its developer.",
          "You may not:",
          [
            "Copy or redistribute Continua's assets without permission.",
            "Reverse engineer or modify the application.",
            "Use Continua's branding in a misleading manner.",
            "Create products that intentionally imitate Continua's identity or proprietary features.",
          ],
          "These Terms do not grant ownership of Continua or its intellectual property.",
        ],
      },
      {
        h: "8. User Content",
        blocks: [
          "You retain ownership of the information you voluntarily provide while using Continua.",
          "By submitting content to the Service, you grant Continua a limited license to process, store, and use that information solely for the purpose of operating the Service, including:",
          [
            "Generating AI conversations;",
            "Maintaining character memories;",
            "Synchronizing your account across devices;",
            "Improving continuity between sessions.",
          ],
          "Continua does not claim ownership of your personal content.",
        ],
      },
      {
        h: "9. Payments & Subscriptions",
        blocks: [
          "Premium subscriptions are processed through the Apple App Store or Google Play Store.",
          "By purchasing a Premium subscription, you acknowledge and agree that:",
          [
            "Subscription payments are processed by the respective platform.",
            "Premium subscriptions renew automatically unless cancelled before the renewal date.",
            "Subscription pricing is displayed before purchase.",
            "You may cancel your subscription at any time through your App Store or Google Play account settings.",
            "Cancelling a subscription prevents future renewals but does not automatically refund previous payments.",
          ],
          "Continua does not directly process payment information.",
        ],
      },
      {
        h: "10. Refunds",
        blocks: [
          "Refund requests are handled by the platform through which the purchase was made.",
          "If you purchased Premium through:",
          [
            "Apple App Store — refund requests are subject to Apple's refund policies.",
            "Google Play Store — refund requests are subject to Google's refund policies.",
          ],
          "Continua cannot guarantee refunds, as the final decision is made by the respective platform.",
          "If you experience technical issues related to Premium, please contact Support before requesting a refund whenever possible.",
        ],
      },
      {
        h: "11. Availability of the Service",
        blocks: [
          "We strive to keep Continua available and reliable.",
          "However, we cannot guarantee uninterrupted availability.",
          "From time to time, the Service may be unavailable due to:",
          [
            "Maintenance;",
            "Software updates;",
            "Infrastructure failures;",
            "Third-party outages;",
            "Security incidents;",
            "Circumstances beyond our reasonable control.",
          ],
          "We reserve the right to modify, suspend, or discontinue features when necessary to improve or maintain the Service.",
        ],
      },
      {
        h: "12. Account Suspension & Termination",
        blocks: [
          "We may suspend or permanently terminate access to Continua if you:",
          [
            "Seriously violate these Terms;",
            "Attempt to abuse or exploit the Service;",
            "Interfere with the security or stability of Continua;",
            "Engage in fraudulent activity;",
            "Repeatedly violate applicable laws while using the Service.",
          ],
          "Whenever reasonably possible, we will attempt to resolve issues before permanently terminating an account.",
          "Users may also delete their own account at any time through the available account management features.",
          "Account deletion may permanently remove associated character memories, conversation history, and other stored data, subject to our Privacy Policy.",
        ],
      },
      {
        h: "13. Limitation of Liability",
        blocks: [
          "Continua is provided on an \u201cas is\u201d and \u201cas available\u201d basis.",
          "To the maximum extent permitted by applicable law:",
          [
            "We do not guarantee that the Service will always be error-free.",
            "We do not guarantee uninterrupted availability.",
            "AI-generated responses may be inaccurate, incomplete, or unexpected.",
            "Character memories may occasionally become inconsistent or incomplete.",
            "We are not responsible for decisions made based on AI-generated content.",
          ],
          "To the fullest extent permitted by law, Continua shall not be liable for indirect, incidental, special, consequential, or punitive damages arising from the use of the Service.",
        ],
      },
      {
        h: "14. Changes to the Service",
        blocks: [
          "Continua is an evolving product.",
          "We may add, modify, improve, or remove features at any time in order to improve the Service, maintain security, or comply with applicable laws.",
          "Where practical, significant changes will be communicated through the application or other appropriate channels.",
        ],
      },
      {
        h: "15. Changes to These Terms",
        blocks: [
          "We may update these Terms from time to time.",
          "If significant changes are made, we will provide appropriate notice within Continua or by other reasonable means.",
          "Your continued use of Continua after updated Terms become effective constitutes acceptance of the revised Terms.",
          "The most recent version of these Terms will always be available within the application.",
        ],
      },
      {
        h: "16. Governing Law",
        blocks: [
          "These Terms shall be governed by and interpreted in accordance with the applicable laws governing the operation of Continua.",
          "Nothing in these Terms limits any mandatory consumer rights that may apply under the laws of your country or region.",
        ],
      },
      {
        h: "17. Severability",
        blocks: [
          "If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall remain in full force and effect.",
        ],
      },
      {
        h: "18. Entire Agreement",
        blocks: [
          "These Terms, together with the Privacy Policy and any additional policies referenced within Continua, constitute the entire agreement between you and Continua regarding your use of the Service.",
        ],
      },
      {
        h: "19. Contact",
        blocks: [
          "If you have questions regarding these Terms of Service, please contact us through the Support section within Continua.",
          "We value user feedback and will make reasonable efforts to respond to inquiries in a timely manner.",
        ],
      },
    ],
  },
  "ai-disclaimer": {
    title: "AI Disclaimer",
    eyebrow: "Privacy",
    meta: ["Effective Date: 20.07.2026", "Last Updated: 20.07.2026"],
    intro: [
      "Welcome to Continua.",
      "Continua uses artificial intelligence (\u201cAI\u201d) to generate conversations, character interactions, and immersive experiences. This disclaimer explains the nature and limitations of AI-generated content.",
      "By using Continua, you acknowledge and understand the following.",
    ],
    footer: "© Continua. All rights reserved.",
    sections: [
      {
        h: "1. AI-Generated Content",
        blocks: [
          "Conversations within Continua are generated by artificial intelligence.",
          "Although we strive to provide consistent, engaging, and emotionally meaningful experiences, AI-generated responses may occasionally contain inaccuracies, inconsistencies, or unexpected behavior.",
        ],
      },
      {
        h: "2. Fictional Characters",
        blocks: [
          "All companions and characters within Continua are fictional.",
          "Their personalities, memories, emotions, opinions, and responses are generated for storytelling, companionship, entertainment, and relaxation.",
          "They should not be interpreted as real people or authoritative sources of information.",
        ],
      },
      {
        h: "3. Character Memory",
        blocks: [
          "Continua includes a persistent memory system designed to create continuity between conversations.",
          "While we continuously improve memory quality:",
          [
            "Characters may occasionally forget information.",
            "Memories may be incomplete or inaccurate.",
            "Relationship progression may occasionally contain inconsistencies.",
          ],
          "These behaviors are inherent to AI-generated systems and do not necessarily indicate a software malfunction.",
        ],
      },
      {
        h: "4. Not Professional Advice",
        blocks: [
          "Continua is not intended to provide:",
          [
            "Medical advice",
            "Mental health treatment",
            "Psychological counseling",
            "Legal advice",
            "Financial advice",
            "Emergency assistance",
          ],
          "Always seek qualified professionals for matters requiring expert guidance.",
          { note: "If you are experiencing an emergency, contact your local emergency services immediately." },
        ],
      },
      {
        h: "5. User Responsibility",
        blocks: [
          "You remain responsible for your own decisions and actions.",
          "Do not rely solely on AI-generated responses when making important personal, financial, medical, legal, or safety-related decisions.",
        ],
      },
      {
        h: "6. Continuous Improvement",
        blocks: [
          "Artificial intelligence is an evolving technology.",
          "We continuously improve Continua to provide more consistent, reliable, and immersive experiences.",
          "Your feedback helps us improve the quality of AI conversations over time.",
        ],
      },
      {
        h: "7. Contact",
        blocks: [
          "If you have questions regarding AI behavior or believe you have encountered an issue, please contact us through the Support section within Continua.",
        ],
      },
    ],
  },
  support: {
    title: "Support",
    eyebrow: "Support",
    meta: ["Effective Date: 20.07.2026", "Last Updated: 20.07.2026"],
    intro: [
      "Welcome to the Continua Support Center.",
      "If you need assistance, have questions, or would like to report an issue, we're here to help.",
    ],
    footer: "© Continua. All rights reserved.",
    actions: [
      { key: "discord", label: "Discord", sublabel: "Join our community", icon: DiscordIcon },
      { key: "contact", label: "Contact", sublabel: "Coming Soon", icon: Mail },
      { key: "report-bug", label: "Report a Bug", sublabel: "Coming Soon", icon: Bug },
      { key: "feedback", label: "Feedback", sublabel: "Coming Soon", icon: MessageSquare },
    ],
    sections: [
      {
        h: "1. Contact Us",
        blocks: [
          "If you need support regarding your account or the application, please contact us through the available support channels within Continua.",
          "We will make reasonable efforts to respond as quickly as possible.",
        ],
      },
      {
        h: "2. Report a Bug",
        blocks: [
          "If you believe you have encountered a bug, please include as much information as possible, including:",
          [
            "A description of the issue",
            "What happened before the issue occurred",
            "Device model",
            "Operating system version",
            "App version",
            "Screenshots (if available)",
          ],
          "Providing detailed information helps us investigate and resolve issues more efficiently.",
        ],
      },
      {
        h: "3. Feedback & Suggestions",
        blocks: [
          "We welcome feedback from our users.",
          "If you have ideas for new features, improvements, or quality-of-life changes, we'd love to hear them.",
          "Many improvements to Continua may be inspired directly by community feedback.",
        ],
      },
      {
        h: "4. Discord Community",
        blocks: [
          "Join our official Discord community to:",
          [
            "Receive announcements",
            "Discuss Continua with other users",
            "Share feedback",
            "Participate in future testing",
            "Learn about upcoming features",
          ],
          "The Discord link is available within the application.",
        ],
      },
      {
        h: "5. Response Times",
        blocks: [
          "We aim to respond to support requests as quickly as possible.",
          "Response times may vary depending on request volume, weekends, holidays, and the complexity of the issue.",
          "We appreciate your patience and understanding.",
        ],
      },
      {
        h: "6. Continuous Improvement",
        blocks: [
          "Continua is an actively developed application.",
          "New features, improvements, optimizations, and bug fixes are released regularly.",
          "Your feedback plays an important role in helping Continua grow.",
        ],
      },
      {
        h: "Thank You",
        blocks: [
          "Thank you for being part of Continua.",
          "Your support, feedback, and patience help us build a better experience for everyone.",
          "We hope Continua becomes a place you'll enjoy returning to night after night.",
        ],
      },
    ],
  },
};

const Block = ({ block }) => {
  if (typeof block === "string") {
    return <p className="text-sm sm:text-[15px] text-violet-200/75 leading-relaxed mb-3 last:mb-0">{block}</p>;
  }
  if (Array.isArray(block)) {
    return (
      <ul className="mb-3 last:mb-0 space-y-1.5 pl-1">
        {block.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm sm:text-[15px] text-violet-200/75 leading-relaxed">
            <span className="mt-[9px] h-1 w-1 rounded-full bg-violet-300/50 flex-shrink-0" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  if (block.sub) {
    return <h3 className="text-sm uppercase tracking-[0.18em] text-violet-100/90 mt-6 mb-3">{block.sub}</h3>;
  }
  if (block.note) {
    return <p className="text-sm text-violet-100/85 italic mb-3 last:mb-0">{block.note}</p>;
  }
  return null;
};

export default function LegalPage({ doc }) {
  const nav = useNavigate();
  const content = DOCS[doc];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  return (
    <div className="min-h-screen relative px-5 sm:px-6 pt-safe-top pb-safe-bottom text-violet-50">
      <div className="relative z-10 w-full max-w-2xl mx-auto fade-rise">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
          data-testid="legal-back-button"
          className="flex items-center gap-2 text-sm text-violet-300/70 hover:text-violet-100 transition-soft mb-8 sm:mb-10"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back
        </button>

        <p className="text-xs uppercase tracking-[0.32em] text-violet-300/55 mb-4">{content.eyebrow}</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-3" data-testid="legal-page-title">
          {content.title}
        </h1>
        <div className="mb-8 space-y-0.5">
          {content.meta.map((m) => (
            <p key={m} className="text-xs text-violet-300/50">{m}</p>
          ))}
        </div>

        {content.intro && (
          <div className="mb-8" data-testid="legal-intro">
            {content.intro.map((para, i) => (
              <p key={i} className="text-sm sm:text-[15px] text-violet-200/80 leading-relaxed mb-3 last:mb-0">
                {para}
              </p>
            ))}
          </div>
        )}

        {content.actions && (
          <div className="glass rounded-2xl px-2 py-2 mb-8" data-testid="support-actions-card">
            {content.actions.map((a) => (
              <button
                key={a.key}
                type="button"
                data-testid={`support-action-${a.key}`}
                onClick={() => {
                  if (a.key === "discord") {
                    window.open(DISCORD_INVITE_URL, "_blank", "noopener,noreferrer");
                  } else {
                    toast.info("Coming Soon");
                  }
                }}
                className="w-full flex items-start gap-3 px-4 py-4 rounded-2xl text-left transition-colors hover:bg-violet-200/4"
              >
                <a.icon className="w-4 h-4 mt-1 text-violet-200/80 flex-shrink-0" strokeWidth={1.5} />
                <span className="flex-1 min-w-0">
                  <span className="block text-violet-50 text-base">{a.label}</span>
                  <span className="block text-xs text-violet-300/55 truncate mt-0.5">{a.sublabel}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="glass rounded-2xl px-5 py-2 sm:px-8" data-testid={`legal-${doc}-content`}>
          {content.sections.map((s) => (
            <section key={s.h} className="py-6 border-b border-violet-200/10 last:border-b-0">
              <h2 className="font-serif-display text-lg sm:text-xl text-violet-100 mb-3">{s.h}</h2>
              {s.blocks.map((block, i) => (
                <Block key={i} block={block} />
              ))}
            </section>
          ))}
        </div>

        <p className="text-xs text-violet-300/45 text-center mt-10 pb-6">
          {content.footer || (
            <>
              <Link to="/" className="hover:text-violet-200 transition-soft">Continua</Link>
              {" · a place that remembers you"}
            </>
          )}
        </p>
      </div>
    </div>
  );
}
